import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  Brain,
  Loader2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  AlertOctagon,
  RefreshCw,
  Info,
} from 'lucide-react';
import { api } from '../../../lib/api';

// Types for iteration mappings (visual comparison)
interface IssueMappingEntry {
  issueId: string;
  description: string;
  severity: string;
  iterations: Array<{
    blindCode: string;
    present: boolean;
    exactText?: string;
  }>;
  overlapStatus: 'all' | 'most' | 'some' | 'one';
}

interface CriteriaMappingEntry {
  field: string;
  iterations: Array<{
    blindCode: string;
    present: boolean;
    value: string | null;
    priority: string | null;
  }>;
  consistencyStatus: 'identical' | 'similar' | 'different' | 'missing';
  valueVariance?: string;
}

// Type for the LLM analysis response
interface Phase1Analysis {
  overallStability: 'HIGHLY_STABLE' | 'STABLE' | 'MODERATE' | 'UNSTABLE' | 'HIGHLY_UNSTABLE';
  stabilityScore: number;
  summary: string;
  dimensions: {
    suitabilityAgreement: {
      status: string;
      agreementPercentage: number;
      values: string[];
      finding: string;
    };
    confidenceVariance: {
      status: string;
      min: number;
      max: number;
      range: number;
      standardDeviation: number;
      finding: string;
    };
    issueOverlap: {
      status: string;
      jaccardSimilarity: number;
      commonIssues: string[];
      varyingIssues: string[];
      finding: string;
    };
    criteriaConsistency: {
      status: string;
      consistentCriteria: Array<{ field: string; value: string; appearsIn: string }>;
      varyingCriteria: Array<{ field: string; values: string[]; finding: string }>;
      priorityConsistency: number;
      finding: string;
    };
    reasoningConsistency: {
      status: string;
      commonThemes: string[];
      contradictions: string[];
      finding: string;
    };
  };
  // Detailed mappings for visual comparison
  mappings?: {
    issues: IssueMappingEntry[];
    criteria: CriteriaMappingEntry[];
  };
  keyDifferences: string[];
  clinicalImpact: {
    wouldAffectReferral: boolean;
    explanation: string;
  };
  recommendations: string[];
}

interface LLMAnalysisResponse {
  testRunId: string;
  evaluatedModel: string;
  iterationCount: number;
  analysis?: Phase1Analysis;
  cached?: boolean;
  status?: 'running' | 'completed';
  startedAt?: string;
  elapsedSeconds?: number;
  message?: string;
}

interface LLMConsistencyAnalysisProps {
  testRunId: string;
  onAnalysisComplete?: (analysis: Phase1Analysis) => void;
  /** Show only the summary metrics without detailed analysis */
  summaryOnly?: boolean;
}

// Model display names
const MODEL_DISPLAY_NAMES: Record<string, string> = {
  CLAUDE_OPUS_4_5: 'Claude Opus 4.5',
  CLAUDE_OPUS_4_6: 'Claude Opus 4.6',
  CLAUDE_SONNET_4: 'Claude Sonnet 4',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4',
  GPT_5_2_PRO: 'GPT-5.2 Pro',
  'gpt-4o': 'GPT-4o',
  GEMINI_3_PRO: 'Gemini 3 Pro',
};

function getModelDisplayName(model: string): string {
  return MODEL_DISPLAY_NAMES[model] || model;
}

// Determine which model is used for analysis based on evaluated model
function getAnalysisModel(evaluatedModel: string): { name: string; provider: string } {
  const lowerModel = evaluatedModel.toLowerCase();
  if (lowerModel.includes('claude')) {
    return { name: 'GPT-5.2 Pro', provider: 'OpenAI' };
  }
  if (lowerModel.includes('gpt') || lowerModel.includes('openai')) {
    return { name: 'Claude Sonnet 4', provider: 'Anthropic' };
  }
  // Default to OpenAI for other models (like Gemini)
  return { name: 'GPT-5.2 Pro', provider: 'OpenAI' };
}

function getStabilityColor(stability: string) {
  switch (stability) {
    case 'HIGHLY_STABLE':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'STABLE':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'MODERATE':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'UNSTABLE':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'HIGHLY_UNSTABLE':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

function getStabilityIcon(stability: string) {
  switch (stability) {
    case 'HIGHLY_STABLE':
    case 'STABLE':
      return CheckCircle;
    case 'MODERATE':
      return AlertTriangle;
    case 'UNSTABLE':
    case 'HIGHLY_UNSTABLE':
      return XCircle;
    default:
      return Info;
  }
}

function getStatusIcon(status: string) {
  if (status === 'consistent' || status === 'low' || status === 'high') {
    return CheckCircle;
  }
  if (status === 'partial' || status === 'moderate' || status === 'varies') {
    return AlertTriangle;
  }
  return XCircle;
}

function getStatusColor(status: string) {
  if (status === 'consistent' || status === 'low' || status === 'high') {
    return 'text-green-600';
  }
  if (status === 'partial' || status === 'moderate' || status === 'varies') {
    return 'text-amber-600';
  }
  return 'text-red-600';
}

// Helper for overlap status colors
function getOverlapColor(status: string) {
  switch (status) {
    case 'all':
      return 'bg-green-100 text-green-700';
    case 'most':
      return 'bg-blue-100 text-blue-700';
    case 'some':
      return 'bg-amber-100 text-amber-700';
    case 'one':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// Helper for consistency status colors
function getConsistencyColor(status: string) {
  switch (status) {
    case 'identical':
      return 'bg-green-100 text-green-700';
    case 'similar':
      return 'bg-blue-100 text-blue-700';
    case 'different':
      return 'bg-amber-100 text-amber-700';
    case 'missing':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// Helper for severity colors
function getSeverityColor(severity: string) {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'HIGH':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'MEDIUM':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'LOW':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

// Helper for priority colors
function getPriorityColor(priority: string | null) {
  switch (priority?.toUpperCase()) {
    case 'CRITICAL':
      return 'text-red-600';
    case 'HIGH':
      return 'text-orange-600';
    case 'MEDIUM':
      return 'text-amber-600';
    case 'LOW':
      return 'text-gray-600';
    default:
      return 'text-gray-500';
  }
}

// Issue Mapping Matrix Component
function IssueMappingMatrix({ issues }: { issues: IssueMappingEntry[] }) {
  if (!issues || issues.length === 0) return null;

  // Get unique iterations from first issue
  const iterations = issues[0]?.iterations.map((i) => i.blindCode) || [];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-3 py-2 text-left font-medium text-gray-700">Issue</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Severity</th>
            {iterations.map((iter) => (
              <th key={iter} className="px-3 py-2 text-center font-medium text-gray-700 whitespace-nowrap">
                {iter}
              </th>
            ))}
            <th className="px-3 py-2 text-center font-medium text-gray-700">Coverage</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue, idx) => (
            <tr key={issue.issueId || idx} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-2 max-w-xs">
                <span className="text-gray-800 line-clamp-2" title={issue.description}>
                  {issue.description}
                </span>
              </td>
              <td className="px-3 py-2">
                <span className={clsx('inline-flex px-2 py-0.5 rounded text-xs font-medium border', getSeverityColor(issue.severity))}>
                  {issue.severity}
                </span>
              </td>
              {issue.iterations.map((iter, iterIdx) => (
                <td key={iterIdx} className="px-3 py-2 text-center">
                  {iter.present ? (
                    <div className="flex justify-center" title={iter.exactText || issue.description}>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  ) : (
                    <div className="flex justify-center">
                      <XCircle className="h-5 w-5 text-gray-300" />
                    </div>
                  )}
                </td>
              ))}
              <td className="px-3 py-2 text-center">
                <span className={clsx('inline-flex px-2 py-0.5 rounded text-xs font-medium', getOverlapColor(issue.overlapStatus))}>
                  {issue.overlapStatus === 'all' && 'All'}
                  {issue.overlapStatus === 'most' && 'Most'}
                  {issue.overlapStatus === 'some' && 'Some'}
                  {issue.overlapStatus === 'one' && 'One'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Criteria Mapping Matrix Component
function CriteriaMappingMatrix({ criteria }: { criteria: CriteriaMappingEntry[] }) {
  if (!criteria || criteria.length === 0) return null;

  // Get unique iterations from first criteria
  const iterations = criteria[0]?.iterations.map((i) => i.blindCode) || [];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-3 py-2 text-left font-medium text-gray-700">Criteria Field</th>
            {iterations.map((iter) => (
              <th key={iter} className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">
                {iter}
              </th>
            ))}
            <th className="px-3 py-2 text-center font-medium text-gray-700">Status</th>
          </tr>
        </thead>
        <tbody>
          {criteria.map((crit, idx) => (
            <tr key={crit.field || idx} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-2 font-medium text-gray-800">{crit.field}</td>
              {crit.iterations.map((iter, iterIdx) => (
                <td key={iterIdx} className="px-3 py-2">
                  {iter.present ? (
                    <div>
                      <span className="text-gray-800 text-xs block truncate max-w-32" title={iter.value || ''}>
                        {iter.value || '-'}
                      </span>
                      {iter.priority && (
                        <span className={clsx('text-xs', getPriorityColor(iter.priority))}>
                          {iter.priority}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
              ))}
              <td className="px-3 py-2 text-center">
                <span className={clsx('inline-flex px-2 py-0.5 rounded text-xs font-medium', getConsistencyColor(crit.consistencyStatus))}>
                  {crit.consistencyStatus === 'identical' && 'Identical'}
                  {crit.consistencyStatus === 'similar' && 'Similar'}
                  {crit.consistencyStatus === 'different' && 'Different'}
                  {crit.consistencyStatus === 'missing' && 'Missing'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Show variance notes for different criteria */}
      {criteria.some((c) => c.valueVariance && c.consistencyStatus !== 'identical') && (
        <div className="mt-3 space-y-1">
          {criteria
            .filter((c) => c.valueVariance && c.consistencyStatus !== 'identical')
            .map((c, idx) => (
              <p key={idx} className="text-xs text-gray-500 flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-medium">{c.field}:</span> {c.valueVariance}
                </span>
              </p>
            ))}
        </div>
      )}
    </div>
  );
}

export function LLMConsistencyAnalysis({ testRunId, onAnalysisComplete, summaryOnly = false }: LLMConsistencyAnalysisProps) {
  const [analysis, setAnalysis] = useState<Phase1Analysis | null>(null);
  const [evaluatedModel, setEvaluatedModel] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const queryClient = useQueryClient();

  // Fetch analysis (uses cache by default)
  const analysisMutation = useMutation({
    mutationFn: async (forceRefresh: boolean = false) => {
      const url = `/api/validation/test-runs/${testRunId}/consistency-analysis/phase1${forceRefresh ? '?refresh=true' : ''}`;
      const response = await api.get<LLMAnalysisResponse>(url);
      return response;
    },
    onSuccess: (data) => {
      // Check if analysis is running on server (async mode)
      if (data.status === 'running') {
        setIsPolling(true);
        setEvaluatedModel(data.evaluatedModel);
        setElapsedSeconds(data.elapsedSeconds || 0);
        return; // Don't set analysis yet, will poll
      }

      // Analysis is complete
      setIsPolling(false);
      if (data.analysis) {
        setAnalysis(data.analysis);
        setEvaluatedModel(data.evaluatedModel);
        setIsCached(data.cached ?? false);
        onAnalysisComplete?.(data.analysis);
        queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', testRunId] });
      }
    },
  });

  // Poll for results when analysis is running on server
  useEffect(() => {
    if (!isPolling) return;

    const pollInterval = setInterval(() => {
      setElapsedSeconds(prev => prev + 5);
      analysisMutation.mutate(false);
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [isPolling, testRunId]);

  // Auto-trigger analysis on mount (will use cache if available)
  useEffect(() => {
    if (!hasStarted && !analysis) {
      setHasStarted(true);
      analysisMutation.mutate(false); // Don't force refresh on initial load
    }
  }, [hasStarted, analysis]);

  if (!analysis) {
    return (
      <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-full bg-purple-100 p-2">
            <Brain className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">AI Consistency Analysis</h2>
            <p className="text-sm text-gray-500">
              Independent analysis of consistency across iterations
            </p>
          </div>
        </div>

        {analysisMutation.isError ? (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-center gap-2 text-red-700 mb-2">
              <AlertOctagon className="h-5 w-5" />
              <span className="font-medium">Analysis failed</span>
            </div>
            <p className="text-sm text-red-600 mb-3">
              {analysisMutation.error instanceof Error
                ? analysisMutation.error.message
                : 'Unable to run consistency analysis'}
            </p>
            <button
              onClick={() => analysisMutation.mutate(false)}
              disabled={analysisMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {analysisMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Retry Analysis
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-purple-50 p-4">
              <div className="flex items-start gap-2">
                <Sparkles className="h-5 w-5 text-purple-500 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium">Cross-model consistency analysis</p>
                  <p className="mt-1 text-purple-700">
                    An independent AI model analyzes the consistency of outputs to provide unbiased metrics.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center gap-2 py-6 text-purple-600">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="font-medium">
                {isPolling
                  ? `Analyzing consistency across iterations... (${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s)`
                  : 'Analyzing consistency across iterations...'}
              </span>
              {isPolling && elapsedSeconds > 60 && (
                <span className="text-sm text-purple-500">
                  This analysis can take 5-15 minutes for complex cases
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const StabilityIcon = getStabilityIcon(analysis.overallStability);

  const analysisModelInfo = evaluatedModel ? getAnalysisModel(evaluatedModel) : null;

  // Summary-only mode: compact display without detailed analysis
  if (summaryOnly) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Consistency Summary</h3>
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ml-auto',
              getStabilityColor(analysis.overallStability)
            )}
          >
            <StabilityIcon className="h-3.5 w-3.5" />
            {analysis.overallStability.replace('_', ' ')}
          </span>
        </div>

        {/* 4 Metric Cards in Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Suitability Agreement */}
          <div className={clsx(
            'rounded-lg border p-4',
            analysis.dimensions.suitabilityAgreement.status === 'consistent' || analysis.dimensions.suitabilityAgreement.status === 'high'
              ? 'border-green-200 bg-green-50'
              : analysis.dimensions.suitabilityAgreement.status === 'partial' || analysis.dimensions.suitabilityAgreement.status === 'varies'
              ? 'border-amber-200 bg-amber-50'
              : 'border-red-200 bg-red-50'
          )}>
            <p className="text-xs font-medium uppercase tracking-wide opacity-75">Suitability</p>
            <p className="mt-1 text-2xl font-bold">
              {analysis.dimensions.suitabilityAgreement.agreementPercentage}
              <span className="text-lg font-normal opacity-75">%</span>
            </p>
            <p className="mt-1 text-xs opacity-75">
              Values: {[...new Set(analysis.dimensions.suitabilityAgreement.values)].join(', ')}
            </p>
          </div>

          {/* Confidence Variance */}
          <div className={clsx(
            'rounded-lg border p-4',
            analysis.dimensions.confidenceVariance.status === 'low' || analysis.dimensions.confidenceVariance.status === 'consistent'
              ? 'border-green-200 bg-green-50'
              : analysis.dimensions.confidenceVariance.status === 'moderate' || analysis.dimensions.confidenceVariance.status === 'varies'
              ? 'border-amber-200 bg-amber-50'
              : 'border-red-200 bg-red-50'
          )}>
            <p className="text-xs font-medium uppercase tracking-wide opacity-75">Confidence</p>
            <p className="mt-1 text-2xl font-bold">
              {analysis.dimensions.confidenceVariance.range}
              <span className="text-lg font-normal opacity-75">% range</span>
            </p>
            <p className="mt-1 text-xs opacity-75">
              {analysis.dimensions.confidenceVariance.min}% - {analysis.dimensions.confidenceVariance.max}%
            </p>
          </div>

          {/* Issue Overlap */}
          <div className={clsx(
            'rounded-lg border p-4',
            analysis.dimensions.issueOverlap.status === 'high' || analysis.dimensions.issueOverlap.status === 'consistent'
              ? 'border-green-200 bg-green-50'
              : analysis.dimensions.issueOverlap.status === 'partial' || analysis.dimensions.issueOverlap.status === 'moderate'
              ? 'border-amber-200 bg-amber-50'
              : 'border-red-200 bg-red-50'
          )}>
            <p className="text-xs font-medium uppercase tracking-wide opacity-75">Issue Overlap</p>
            <p className="mt-1 text-2xl font-bold">
              {Math.round(analysis.dimensions.issueOverlap.jaccardSimilarity * 100)}
              <span className="text-lg font-normal opacity-75">%</span>
            </p>
            <p className="mt-1 text-xs opacity-75">Same issues found across runs</p>
          </div>

          {/* Criteria Consistency */}
          <div className={clsx(
            'rounded-lg border p-4',
            analysis.dimensions.criteriaConsistency.status === 'consistent' || analysis.dimensions.criteriaConsistency.status === 'high'
              ? 'border-green-200 bg-green-50'
              : analysis.dimensions.criteriaConsistency.status === 'partial' || analysis.dimensions.criteriaConsistency.status === 'varies'
              ? 'border-amber-200 bg-amber-50'
              : 'border-red-200 bg-red-50'
          )}>
            <p className="text-xs font-medium uppercase tracking-wide opacity-75">Criteria Match</p>
            <p className="mt-1 text-2xl font-bold">
              {Math.round(analysis.dimensions.criteriaConsistency.priorityConsistency * 100)}
              <span className="text-lg font-normal opacity-75">%</span>
            </p>
            <p className="mt-1 text-xs opacity-75">Priority consistency across runs</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-purple-200 bg-white overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-white hover:from-purple-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-purple-100 p-2">
            <Brain className="h-5 w-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-gray-900">AI Consistency Analysis</h2>
            <p className="text-sm text-gray-500">
              {evaluatedModel && analysisModelInfo ? (
                <>
                  <span className="font-medium text-gray-700">{getModelDisplayName(evaluatedModel)}</span>
                  {' evaluated by '}
                  <span className="font-medium text-purple-600">{analysisModelInfo.name}</span>
                  {' '}({analysisModelInfo.provider})
                </>
              ) : (
                'Independent analysis by a different AI model'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border',
              getStabilityColor(analysis.overallStability)
            )}
          >
            <StabilityIcon className="h-4 w-4" />
            {analysis.overallStability.replace('_', ' ')}
          </span>
          <span className="text-lg font-bold text-purple-600">{analysis.stabilityScore}%</span>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="p-6 space-y-6 border-t border-purple-100">
          {/* Summary */}
          <div className="rounded-lg bg-gray-50 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
            <p className="text-sm text-gray-600">{analysis.summary}</p>
          </div>

          {/* Dimensions Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Suitability Agreement */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Suitability Agreement</h4>
                <div className={clsx('flex items-center gap-1', getStatusColor(analysis.dimensions.suitabilityAgreement.status))}>
                  {(() => {
                    const Icon = getStatusIcon(analysis.dimensions.suitabilityAgreement.status);
                    return <Icon className="h-4 w-4" />;
                  })()}
                  <span className="text-sm font-medium">{analysis.dimensions.suitabilityAgreement.agreementPercentage}%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500">{analysis.dimensions.suitabilityAgreement.finding}</p>
            </div>

            {/* Confidence Variance */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Confidence Variance</h4>
                <div className={clsx('flex items-center gap-1', getStatusColor(analysis.dimensions.confidenceVariance.status))}>
                  {(() => {
                    const Icon = getStatusIcon(analysis.dimensions.confidenceVariance.status);
                    return <Icon className="h-4 w-4" />;
                  })()}
                  <span className="text-sm font-medium">
                    {analysis.dimensions.confidenceVariance.min}-{analysis.dimensions.confidenceVariance.max}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500">{analysis.dimensions.confidenceVariance.finding}</p>
            </div>

            {/* Issue Overlap */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Issue Detection Overlap</h4>
                <div className={clsx('flex items-center gap-1', getStatusColor(analysis.dimensions.issueOverlap.status))}>
                  {(() => {
                    const Icon = getStatusIcon(analysis.dimensions.issueOverlap.status);
                    return <Icon className="h-4 w-4" />;
                  })()}
                  <span className="text-sm font-medium">
                    {Math.round(analysis.dimensions.issueOverlap.jaccardSimilarity * 100)}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500">{analysis.dimensions.issueOverlap.finding}</p>
            </div>

            {/* Criteria Consistency */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Criteria Consistency</h4>
                <div className={clsx('flex items-center gap-1', getStatusColor(analysis.dimensions.criteriaConsistency.status))}>
                  {(() => {
                    const Icon = getStatusIcon(analysis.dimensions.criteriaConsistency.status);
                    return <Icon className="h-4 w-4" />;
                  })()}
                  <span className="text-sm font-medium">
                    {Math.round(analysis.dimensions.criteriaConsistency.priorityConsistency * 100)}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500">{analysis.dimensions.criteriaConsistency.finding}</p>
            </div>
          </div>

          {/* Issue Mapping Matrix */}
          {analysis.mappings?.issues && analysis.mappings.issues.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <AlertOctagon className="h-4 w-4 text-amber-600" />
                Issue Detection Across Iterations
              </h3>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <IssueMappingMatrix issues={analysis.mappings.issues} />
              </div>
            </div>
          )}

          {/* Criteria Mapping Matrix */}
          {analysis.mappings?.criteria && analysis.mappings.criteria.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600" />
                Search Criteria Across Iterations
              </h3>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <CriteriaMappingMatrix criteria={analysis.mappings.criteria} />
              </div>
            </div>
          )}

          {/* Key Differences */}
          {analysis.keyDifferences.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Key Differences Identified</h3>
              <ul className="space-y-1">
                {analysis.keyDifferences.map((diff, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    {diff}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Clinical Impact */}
          <div className={clsx(
            'rounded-lg p-4',
            analysis.clinicalImpact.wouldAffectReferral
              ? 'bg-red-50 border border-red-200'
              : 'bg-green-50 border border-green-200'
          )}>
            <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
              Clinical Impact Assessment
              {analysis.clinicalImpact.wouldAffectReferral ? (
                <span className="text-xs text-red-600 font-normal">(Would affect referral)</span>
              ) : (
                <span className="text-xs text-green-600 font-normal">(No impact on referral)</span>
              )}
            </h3>
            <p className="text-sm text-gray-600">{analysis.clinicalImpact.explanation}</p>
          </div>

          {/* Recommendations */}
          {analysis.recommendations.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Recommendations</h3>
              <ul className="space-y-1">
                {analysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Re-run Analysis */}
          <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isCached && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  Cached result
                </span>
              )}
            </div>
            <button
              onClick={() => analysisMutation.mutate(true)} // Force refresh
              disabled={analysisMutation.isPending}
              className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 disabled:opacity-50"
            >
              {analysisMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Re-run analysis
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
